import type { FC, ReactNode } from 'react'

import { ArrowDown, Play } from 'lucide-react'
import CtaLink from '@/components/generals/cta-link'
import LayoutDemo from '@/components/landing/layout-demo'
import { appLinks } from '@/constants/site'

const PLATFORMS = ['Android', 'Tablet & foldable']

const STATS = [
    { value: '1, 2 and 4', label: 'views free' },
    { value: 'Up to 12', label: 'views with PRO' },
    { value: 'Local MP4', label: 'to share' },
    { value: 'Google TV', label: 'on your local network' }
]

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='top' aria-labelledby='hero-title' className='relative overflow-hidden'>
            <div aria-hidden='true' className='hero-glow pointer-events-none absolute inset-0' />

            <div className='relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-14 pt-10 sm:px-8 sm:pt-14 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.1fr)] lg:gap-14 lg:pb-20 lg:pt-16'>
                <div className='animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out'>
                    <p className='inline-flex items-center gap-2 text-[0.72rem] font-bold uppercase tracking-[0.16em] text-brand'>
                        <span aria-hidden='true' className='size-1.5 rounded-full bg-brand' />
                        Multi-view player for Android
                    </p>

                    <h1
                        id='hero-title'
                        className='mt-6 font-heading text-[clamp(3.1rem,8.6vw,5.6rem)] font-extrabold leading-[0.95] tracking-[-0.045em] text-foreground lg:text-[clamp(3.2rem,6vw,5.4rem)]'
                    >
                        <span className='block'>
                            Many videos.
                        </span>

                        <span className='text-gradient block pb-[0.08em]'>
                            One screen.
                        </span>
                    </h1>

                    <p className='mt-7 max-w-[34rem] text-lg leading-relaxed text-muted-foreground sm:text-[1.2rem]'>
                        Play, compare and organize videos and photos on the same screen. Fine-tune every shot and export local scenes to MP4.
                    </p>

                    <div className='mt-9 flex flex-wrap items-center gap-3'>
                        <CtaLink
                            href={appLinks.playStore}
                            external
                            leadingIcon={<Play className='size-4' fill='currentColor' />}
                        >
                            Get it on Google Play
                        </CtaLink>

                        <CtaLink
                            href='#demo'
                            variant='outline'
                            trailingIcon={<ArrowDown className='size-4 transition-transform duration-200 group-hover:translate-y-0.5' />}
                        >
                            Try layouts
                        </CtaLink>
                    </div>

                    <p className='mt-6 text-sm leading-relaxed text-muted-foreground'>
                        Classic 1, 2 and 4-view grids for free.
                        <br />
                        No ads during local playback.
                    </p>

                    <ul className='mt-6 flex flex-wrap gap-2' aria-label='Supported devices'>
                        {PLATFORMS.map((platform) => (
                            <li
                                key={platform}
                                className='rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground/85'
                            >
                                {platform}
                            </li>
                        ))}

                        <li className='rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground/85'>
                            Google TV
                            <span className='ml-1.5 text-[0.68rem] font-normal text-muted-foreground'>
                                plan-based
                            </span>
                        </li>
                    </ul>
                </div>

                <div className='animate-in fade-in slide-in-from-bottom-6 duration-1000 ease-out'>
                    <LayoutDemo />
                </div>
            </div>

            <div className='relative mx-auto max-w-7xl px-5 sm:px-8'>
                <ul className='grid grid-cols-2 gap-x-6 gap-y-7 border-t border-border py-9 lg:grid-cols-4'>
                    {STATS.map((stat) => (
                        <li key={stat.value}>
                            <p className='font-heading text-xl font-bold tracking-[-0.02em] text-foreground sm:text-[1.35rem]'>
                                {stat.value}
                            </p>

                            <p className='mt-1 text-sm text-muted-foreground'>
                                {stat.label}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default HeroSection
